import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { DEBOUNCE_TIME_FAST } from "~/common/constants";
import {
  GetEvidenceUsersForSelectFormDocument,
  type GetEvidenceUsersForSelectFormQuery,
  type GetEvidenceUsersForSelectFormQueryVariables,
} from "~/features/evidences/gqls";
import type { EvidenceUserOption } from "~/features/evidences/types";
import { useTenant } from "~/features/tenant/context";

const toOption = (u: { id: string; displayName?: string | null }): EvidenceUserOption => ({
  id: u.id,
  displayName: u.displayName ?? u.id,
});

/**
 * Debounced async user search for the evidence list filter. Returns the current
 * `options`, a `loading` flag, `search`/`reset`, and `resolveUser` to look up a
 * single user by id (used to label a pre-selected filter on reload).
 */
export function useEvidenceUserSearch() {
  const { tenantId } = useTenant();
  const [options, setOptions] = useState<EvidenceUserOption[]>([]);
  const [getUsers, { loading }] = useLazyQuery<
    GetEvidenceUsersForSelectFormQuery,
    GetEvidenceUsersForSelectFormQueryVariables
  >(GetEvidenceUsersForSelectFormDocument);

  const search = useDebouncedCallback((value: string) => {
    if (!tenantId || !value) {
      setOptions([]);
      return;
    }
    void getUsers({ variables: { tenantId, where: { displayName: { _ilike: `%${value}%` } } } }).then(({ data }) =>
      setOptions((data?.users ?? []).map(toOption)),
    );
  }, DEBOUNCE_TIME_FAST);

  const reset = useCallback(() => setOptions([]), []);

  const resolveUser = useCallback(
    async (id: string): Promise<EvidenceUserOption | null> => {
      if (!tenantId) return null;
      const { data } = await getUsers({ variables: { tenantId, where: { id: { _eq: id } } } });
      const user = data?.users?.[0];
      return user ? toOption(user) : null;
    },
    [getUsers, tenantId],
  );

  return { options, loading, search, reset, resolveUser };
}
